/**
 * Centralized user role definitions
 * This enum should be used throughout the application instead of hardcoded strings
 *
 * MULTI-TENANT ARCHITECTURE:
 * - UserRole: Platform-level roles (stored in User.role)
 * - OrgRole: Organization-level roles (stored in OrganizationMembership.role)
 */

/**
 * Platform-level user roles (stored in User.role)
 * These are used for portal access and platform-wide permissions
 */
export enum UserRole {
  // Platform admin with cross-org access
  LYFE_ADMIN = "LYFE_ADMIN",

  // Legacy roles for backward compatibility
  PROVIDER = "PROVIDER",
  ADMIN = "ADMIN",
  DD_ANALYST = "DD_ANALYST",
  NURSE = "NURSE",
  STAFF = "STAFF",

  // Portal users (not part of organizations)
  PATIENT = "PATIENT",
  FAMILY = "FAMILY",
}

/**
 * Organization membership roles (assigned per organization)
 * Values match Prisma OrgMemberRole enum for direct comparison
 */
export enum OrgRole {
  CLINIC_ADMIN = "CLINIC_ADMIN",
  CLINIC_PROVIDER = "CLINIC_PROVIDER",
  CLINIC_STAFF = "CLINIC_STAFF",
}

/**
 * Organization type - determines environment and data sources
 */
export enum OrgType {
  DEMO = "DEMO",
  PRODUCTION = "PRODUCTION",
}

/**
 * Organization status
 */
export enum OrgStatus {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  TRIAL = "TRIAL",
  INACTIVE = "INACTIVE",
}

/**
 * Organization metadata stored in Organization.metadata JSON field
 */
export interface OrganizationMetadata {
  type: OrgType;
  zusEnvironment: "sandbox" | "production";
  drchronoEnvironment?: "sandbox" | "production";
  features: string[];
  maxPatients: number;
  maxProviders: number;
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
  };
  // Encrypted credentials - secrets stored encrypted
  zusClientId?: string;
  zusClientSecretEncrypted?: string; // Encrypted with lib/encryption.ts
  drchronoClientId?: string;
  drchronoClientSecretEncrypted?: string; // Encrypted with lib/encryption.ts
  // DrChrono OAuth Tokens (encrypted) - stored in Clerk Organization publicMetadata
  drchronoAccessTokenEncrypted?: string; // Encrypted with lib/encryption.ts
  drchronoRefreshTokenEncrypted?: string; // Encrypted with lib/encryption.ts
  drchronoTokenExpiresAt?: string; // ISO timestamp
  drchronoStatus?: "DISCONNECTED" | "CONNECTING" | "CONNECTED" | "ERROR" | "EXPIRED";
  drchronoLastSyncAt?: string; // ISO timestamp
  drchronoConnectedBy?: string; // User ID who connected
  drchronoConnectedAt?: string; // ISO timestamp
  drchronoLastTokenRefresh?: string; // ISO timestamp
  drchronoTokenRefreshCount?: number; // Number of times token was refreshed
  // Audit fields for credential changes
  credentialsUpdatedAt?: string;
  credentialsUpdatedBy?: string;
}

/**
 * User public metadata
 */
export interface UserPublicMetadata {
  role?: UserRole;
  isLyfeAdmin?: boolean;
  defaultOrgId?: string;
  patientId?: string; // For PATIENT role users
}

// ============================================================================
// NOTE: Runtime functions are in lib/utils/user-roles.ts
// Import utility functions directly from there to avoid circular dependencies:
//   import { getUserRoleLabel, isAdminRole } from "@/lib/utils/user-roles";
// ============================================================================
